<template>
  <div class="recommendations-container p-8">
    <div class="header mb-10">
      <div class="flex justify-between items-center mb-4">
        <div class="flex flex-col justify-start items-start">
          <h2 class="text-3xl font-bold text-primary mb-2">Talent Recommendations</h2>
          <p class="text-gray-600 text-lg">{{ $t('orgDashboard.users.recommendationsDescription') }}</p>
        </div>

        <div class="flex items-center gap-4">
          <!-- View Toggle Button -->
          <vs-tooltip 
          :text="viewMode === 'simple' ? 'Simple View' : 'Detailed View'"
          position="top"
        >
          <vs-button 
            color="warning" 
            type="filled" 
            class="view-toggle-btn"
            @click="viewMode = viewMode === 'simple' ? 'detailed' : 'simple'"
          >
            <i 
              class="feather text-xl" 
              :class="viewMode === 'simple' ? 'icon-grid' : 'icon-list'"
            ></i>
          </vs-button>
        </vs-tooltip>
        </div>
      </div>
    </div>

    <!-- SIMPLE VIEW (Default List View) -->
    <div v-if="viewMode === 'simple'" class="flex flex-col gap-4 mb-12">
      <!-- Fitment Plan Row -->
      <div class="simple-list-row" @click="openActivationModal('Fitment Plan', 'plan_fitment_001', 'coaching.svg')">
        <div class="flex items-center gap-5">
          <div class="flex-shrink-0 flex items-center justify-center w-12 h-12">
            <img src="@/assets/Iconography/App Icons/coaching.svg" alt="Coaching" class="w-10 h-10 object-contain">
          </div>
          <div class="flex flex-col justify-center text-left">
            <h3 class="text-xl font-bold text-gray-800 mb-1 leading-tight">{{ $t('orgDashboard.users.fitmentPlan') }}</h3>
            <span class="text-gray-500 text-sm">{{ $t('orgDashboard.users.fitmentPlanDescription') }}</span>
          </div>
        </div>
        <div class="flex-shrink-0 flex items-center">
           <i class="feather icon-chevron-right text-gray-400 text-2xl"></i>
        </div>
      </div>

      <!-- Development Plan Row -->
      <div class="simple-list-row" @click="openActivationModal('Development Plan', 'plan_dev_001', 'bot.svg')">
        <div class="flex items-center gap-5">
          <div class="flex-shrink-0 flex items-center justify-center w-12 h-12">
            <img src="@/assets/Iconography/App Icons/bot.svg" alt="ALEX Bot" class="w-10 h-10 object-contain">
          </div>
          <div class="flex flex-col justify-center text-left">
            <h3 class="text-xl font-bold text-gray-800 mb-1 leading-tight">{{ $t('orgDashboard.users.developmentPlan') }}</h3>
            <span class="text-gray-500 text-sm">{{ $t('orgDashboard.users.developmentPlanDescription') }}</span>
          </div>
        </div>
        <div class="flex-shrink-0 flex items-center">
           <i class="feather icon-chevron-right text-gray-400 text-2xl"></i>
        </div>
      </div>

      <!-- Succession Planning Row -->
      <div class="simple-list-row" @click="openActivationModal('Succession Plan', 'plan_suc_001', 'rewards.svg')">
        <div class="flex items-center gap-5">
          <div class="flex-shrink-0 flex items-center justify-center w-12 h-12">
            <img src="@/assets/Iconography/App Icons/rewards.svg" alt="Rewards" class="w-10 h-10 object-contain">
          </div>
          <div class="flex flex-col justify-center text-left">
            <h3 class="text-xl font-bold text-gray-800 mb-1 leading-tight">{{ $t('orgDashboard.users.successionPlanning') }}</h3>
            <span class="text-gray-500 text-sm">{{ $t('orgDashboard.users.successionPlanningDescription') }}</span>
          </div>
        </div>
        <div class="flex-shrink-0 flex items-center">
           <i class="feather icon-chevron-right text-gray-400 text-2xl"></i>
        </div>
      </div>
    </div>

    <!-- DETAILED VIEW (Original Grid View) -->
    <div v-else-if="viewMode === 'detailed'" class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
      <!-- Existing Detailed Cards (Kept Exactly as they were) -->
      <div class="recommendation-card" :class="{ 'recommended': primaryRecommendation === 'coaching' }">
        <div class="recommended-tag" v-if="primaryRecommendation === 'coaching'">
          <i class="feather icon-star mr-1"></i> Best Match
        </div>
        <div class="card-header flex items-center mb-6">
          <div class="icon-wrapper">
            <img src="@/assets/Iconography/App Icons/coaching.svg" alt="Coaching" class="w-10 h-10">
          </div>
          <div class="ml-4">
            <h3 class="text-2xl font-bold">{{ $t('orgDashboard.users.fitmentPlan') }}</h3>
            <span class="text-gray-500">{{ $t('orgDashboard.users.fitmentPlanDescription') }}</span>
          </div>
        </div>
        <div class="card-content">
          <p class="text-gray-700 text-lg mb-6">{{ $t('orgDashboard.users.fitmentPlanContent') }}</p>
          <ul class="benefits-list mb-8">
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.roadToRole') }}</li>
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.skillProfiling') }}</li>
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.learningCredits') }}</li>
          </ul>
          <div class="flex-grow"></div>
          <vs-button class="w-full action-button mt-auto" color="warning" @click="openActivationModal('Fitment Plan', 'plan_fitment_001', 'coaching.svg')">
            <i class="feather icon-users mr-2"></i> Start Onboarding
          </vs-button>
        </div>
      </div>

      <div class="recommendation-card" :class="{ 'recommended': primaryRecommendation === 'alex' }">
        <div class="card-header flex items-center mb-6">
          <div class="icon-wrapper">
            <img src="@/assets/Iconography/App Icons/bot.svg" alt="ALEX Bot" class="w-10 h-10">
          </div>
          <div class="ml-4">
            <h3 class="text-2xl font-bold">{{ $t('orgDashboard.users.developmentPlan') }}</h3>
            <span class="text-gray-500">{{ $t('orgDashboard.users.developmentPlanDescription') }}</span>
          </div>
        </div>
        <div class="card-content">
          <p class="text-gray-700 text-lg mb-6">{{ $t('orgDashboard.users.developmentPlanContent') }}</p>
          <ul class="benefits-list mb-8">
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.customLearningPaths') }}</li>
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.aiAssistedValidation') }}</li>
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.progressTracking') }}</li>
          </ul>
          <div class="flex-grow"></div>
          <vs-button class="w-full action-button mt-auto" color="warning" @click="openActivationModal('Development Plan', 'plan_dev_001', 'bot.svg')">
            <i class="feather icon-play-circle mr-2"></i> Activate Plan
          </vs-button>
        </div>
      </div>

      <div class="recommendation-card" :class="{ 'recommended': primaryRecommendation === 'rewards' }">
        <div class="recommended-tag" v-if="primaryRecommendation === 'rewards'">
          <i class="feather icon-star mr-1"></i> Best Match
        </div>
        <div class="card-header flex items-center mb-6">
          <div class="icon-wrapper">
            <img src="@/assets/Iconography/App Icons/rewards.svg" alt="Rewards" class="w-10 h-10">
          </div>
          <div class="ml-4">
            <h3 class="text-2xl font-bold">{{ $t('orgDashboard.users.successionPlanning') }}</h3>
            <span class="text-gray-500">{{ $t('orgDashboard.users.successionPlanningDescription') }}</span>
          </div>
        </div>
        <div class="card-content">
          <p class="text-gray-700 text-lg mb-6">{{ $t('orgDashboard.users.successionPlanningContent') }}</p>
          <ul class="benefits-list mb-8">
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.appraisalIndicators') }}</li>
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.goalSettingMentorship') }}</li>
            <li><i class="feather icon-check-circle text-warning"></i>{{ $t('orgDashboard.users.behaviouralCoaching') }}</li>
          </ul>
          <div class="flex-grow"></div>
          <vs-button class="w-full action-button mt-auto" color="warning" @click="openActivationModal('Succession Plan', 'plan_suc_001', 'rewards.svg')">
            <i class="feather icon-award mr-2"></i> Start Promotion
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Snapshot Section -->
    <div class="progress-section bg-white rounded-xl p-8 shadow-sm">
       <div class="flex justify-start items-center gap-6 mb-8">
      <h3 class="text-2xl font-bold">{{ $t('orgDashboard.users.talentCapabilitySnapshot') }}</h3>
      
      <!-- This button will now sit right after the text above -->
      <vs-button class="development-plan-btn shadow-md" color="warning" type="filled" @click="showActivePlansModal = true">
        <i class="feather icon-layers mr-2"></i>
        My Active Plans 
        <span v-if="activePlans.length > 0" class="ml-2 bg-white text-warning rounded-full px-2 text-xs font-bold">{{ activePlans.length }}</span>
      </vs-button>
    </div>
      <div class="progress-stats grid grid-cols-1 md:grid-cols-4 gap-8">
        <div class="stat-card" v-for="(score, key) in kvdrScores" :key="key">
          <div class="flex justify-between items-center mb-3">
            <div class="text-left"><h4 class="font-bold text-xl capitalize">{{ key }}</h4><p class="text-gray-500 text-sm">{{ getScoreLabel(score) }}</p></div>
            <span class="text-2xl font-bold" :class="getScoreColor(score)">{{ score }}%</span>
          </div>
          <div class="progress-bar"><div :style="{ width: `${score}%` }" class="progress-fill" :class="getProgressClass(score)"></div></div>
        </div>
      </div>
    </div>

    <vs-popup class="active-plans-popup" title="Your Active Plans" :active.sync="showActivePlansModal">
      <div class="active-plans-content custom-scroll">
        <div v-if="isLoadingPlans" class="flex flex-col items-center justify-center h-48">
          <i class="feather icon-loader fa-spin text-2xl text-primary mb-2"></i>
          <span class="text-gray-500 text-sm font-medium">Loading your plans...</span>
        </div>
        <div v-else-if="activePlans.length === 0" class="flex flex-col items-center justify-center h-48 text-center">
          <div class="bg-gray-50 rounded-full p-3 mb-2">
             <i class="feather icon-clipboard text-3xl text-gray-300"></i>
          </div>
          <p class="text-gray-600 font-medium text-sm">No active plans found.</p>
        </div>
        <div v-else class="space-y-3 py-1">
          <!-- Changed items-center to items-start and added text-left for alignment -->
          <div v-for="(plan, index) in activePlans" :key="index" class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-md transition-all">
            <div class="flex items-start w-full sm:w-auto mb-3 sm:mb-0 overflow-hidden text-left">
               <vs-avatar class="mr-3 flex-shrink-0" :text="plan.title ? plan.title.substring(0,2).toUpperCase() : '??'" color="warning" text-color="white" size="40px" />
               <div class="min-w-0">
                  <h4 class="font-bold text-base text-gray-800 mb-1 truncate">{{ plan.title || 'Untitled Plan' }}</h4>
                  <div class="text-xs text-gray-500 flex items-center gap-2">
                    <span class="px-1.5 py-px rounded-[4px] font-bold uppercase tracking-wide border" :class="plan.status === 'draft' ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-green-50 text-green-600 border-green-200'" style="font-size: 10px;">
                        {{ plan.status === 'draft' ? 'Draft' : 'Active' }}
                    </span>
                    <span class="flex items-center"><i class="feather icon-users mr-1"></i> {{ (plan.members && plan.members.length > 0) ? plan.members.length : (plan.numberOfUsers || 0) }} Members</span>
                  </div>
               </div>
            </div>
            <!-- Actions Color set to Warning -->
            <div class="flex-shrink-0 flex gap-2 w-full sm:w-auto justify-end items-center">
                <vs-button color="#f1f5f9" text-color="#475569" type="flat" icon="settings" radius size="small" class="action-btn-gray" @click="openEditModal(plan)"></vs-button>
                <vs-button color="warning" type="filled" size="small" @click="goToBoard(plan)">Open <i class="feather icon-arrow-right ml-1"></i></vs-button>
            </div>
          </div>
        </div>
      </div>
      <div class="active-plans-footer">
         <vs-button color="dark" type="border" size="small" @click="showActivePlansModal = false">Close</vs-button>
      </div>
    </vs-popup>

    <!-- Activation/Edit Popup -->
    <vs-popup :title="isEditMode ? 'Manage Plan' : 'Configure ' + selectedPlanName" :active.sync="isModalActive" class="plan-activation-popup">
      <div class="p-2 relative text-left">
        <div v-if="isLoadingBoardDetails" class="absolute inset-0 bg-white/90 z-50 flex flex-col justify-center items-center rounded-lg">
            <i class="feather icon-loader fa-spin text-3xl text-primary mb-2"></i>
            <span class="text-sm font-semibold">Fetching plan members...</span>
        </div>
        <div class="mb-6 flex items-start">
           <vs-button v-if="isEditMode" radius icon="arrow_back" color="dark" type="flat" class="flex-shrink-0 mr-3 -ml-2" tooltip="Back to Plan List" @click="backToPlanList"></vs-button>
           <div>
              <h4 class="font-bold text-gray-800 text-xl">{{ isEditMode ? 'Update Participants' : 'Setting up ' + selectedPlanName }}</h4>
              <p class="text-sm text-gray-500 mt-1">{{ isEditMode ? 'Add or remove employees from this active plan.' : 'This will create a dedicated workspace and enroll selected employees.' }}</p>
           </div>
        </div>
        <div class="mb-6">
           <label class="text-sm font-bold ml-1 text-gray-700">Plan Title</label>
           <vs-input class="w-full mt-1" placeholder="e.g. Q3 Development Plan" v-model="boardTitle" :disabled="isEditMode"/>
        </div>
        <div class="mb-4">
          <label class="text-sm font-bold ml-1 text-gray-700">Select Participants</label>
          <div class="relative mt-1">
             <vs-input icon="search" placeholder="Search employees by name..." v-model="userSearchQuery" class="w-full"/>
          </div>
        </div>
        <!-- ... (Rest of activation popup logic remains same) -->
        <div class="mb-4">
          <p class="text-xs font-semibold text-gray-500 uppercase mb-2 ml-1">Quick Add by Role</p>
          <div class="flex flex-wrap gap-2">
            <div v-for="(role, index) in availableRoles" :key="index" class="role-chip" :class="{'active-role': activeRoleFilters.includes(role)}" :style="getRoleStyle(role)" @click="toggleRoleGroup(role)">
                {{ role }}<i v-if="activeRoleFilters.includes(role)" class="feather icon-check ml-1 text-xs"></i>
            </div>
          </div>
        </div>
        <div class="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden flex flex-col h-64">
           <div class="bg-gray-100 border-b border-gray-200 px-4 py-2 flex justify-between items-center flex-shrink-0 z-10">
              <span class="text-xs font-bold text-gray-500 uppercase">All Employees</span>
              <span class="text-xs font-bold text-primary">{{ selectedUsers.length }} Selected</span>
           </div>
           <div class="overflow-y-auto custom-scroll p-2 flex-grow">
             <div v-for="(user, index) in filteredUsers" :key="index" class="flex items-center justify-between p-3 mb-2 bg-white rounded-lg shadow-sm cursor-pointer border border-transparent hover:border-primary transition-all" @click="toggleUserSelection(user)">
               <div class="flex items-center text-left">
                  <vs-avatar size="36px" class="mr-3 flex-shrink-0" :text="user.initials" color="#64748b"/>
                  <div class="flex flex-col justify-center">
                    <p class="text-sm font-semibold text-gray-800 leading-tight mb-0.5">{{ user.name }}</p>
                    <div class="flex items-center gap-2">
                       <span v-if="user.role" class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{{ user.role }}</span>
                       <p class="text-xs text-gray-400 leading-tight">{{ user.email }}</p>
                    </div>
                  </div>
               </div>
               <div class="pl-3">
                 <i class="feather transition-colors" :class="selectedUsers.includes(user.id) ? 'icon-check-circle text-primary' : 'icon-circle text-gray-300'" style="font-size: 1.4rem;"></i>
               </div>
             </div>
          </div>
        </div>
        <div class="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <vs-button color="dark" type="flat" @click="isModalActive = false" class="mr-2">Cancel</vs-button>
          <vs-button color="success" @click="confirmActivation" :disabled="isLoading || isLoadingBoardDetails">
            <span v-if="isLoading">{{ loadingStatusText }}</span>
            <span v-else-if="isEditMode">Update Members</span>
            <span v-else>Launch Plan <i class="feather icon-arrow-right ml-1"></i></span>
          </vs-button>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
export default {
  name: 'UserRecommendations',
  data() {
    return {
      viewMode: 'simple', // 'simple' defaults to the horizontal list view

      primaryRecommendation: 'alex', 
      selectedIcon: '',
      
      kvdrScores: { competency: 75, strategy: 60, execution: 85, culture: 90 },
      isModalActive: false,
      showActivePlansModal: false, 
      isLoading: false,
      loadingStatusText: 'Processing...',
      
      selectedPlanName: '',
      selectedPlanId: null,
      activePlans: [],
      boardTitle: '',
      
      userSearchQuery: '',
      selectedUsers: [], 
      activeRoleFilters: [],
      
      usersList: [], 
      orgDesignRoles: [], 
      
      isEditMode: false,
      editingBoardId: null,
      originalBoardMembers: [], 
      isLoadingPlans: false,
      isLoadingBoardDetails: false,

      planAppsMap: {
        'Fitment Plan': [],
        'Development Plan': ['alex'], 
        'Succession Plan': ['notes'] 
      },
      defaultApps: [],
      roleColorsCache: {}
    }
  },
  computed: {
    orgData() { return this.$store.state.organization.orgData || {}; },
    userData() { return this.$store.state.users.data || {}; },

    availableRoles() {
      let roles = this.orgDesignRoles;
      roles = roles.filter(role => {
        const r = String(role).toLowerCase();
        return r !== 'talent pool candidate' && r !== 'general' && r !== 'undefined' && r.trim() !== '';
      });
      return [...new Set(roles)].sort();
    },
    
    filteredUsers() {
      let users = this.usersList;
      if (this.userSearchQuery) {
        const query = this.userSearchQuery.toLowerCase();
        users = users.filter(u => u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query));
      }
      if (this.activeRoleFilters.length > 0) {
        users = users.filter(u => {
            const userRole = (u.role || '').toLowerCase();
            return this.activeRoleFilters.some(filter => userRole === filter.toLowerCase());
        });
      }
      return users;
    },
  },
  watch: {
    orgData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal._id) {
          this.initData(newVal._id);
        }
      }
    },
    showActivePlansModal(val) {
        if(val) this.fetchActivePlans();
    }
  },
  methods: {
    async initData(orgId) {
        await this.fetchOrgUsers(orgId);
        await this.fetchRolesAndMapUsers(orgId);
    },

    async fetchActivePlans() {
        if (!this.orgData || !this.orgData._id) return;
        
        if (this.activePlans.length === 0) {
            this.isLoadingPlans = true;
        }

        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
            const orgId = this.orgData._id;
            const url = `https://api.getboarded.tech/boards?organizationId=${orgId}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            if (!response.ok) throw new Error(`API Error: ${response.status}`);
            const res = await response.json();
            
            let allBoards = [];
            if (res && Array.isArray(res.data)) allBoards = res.data;
            else if (Array.isArray(res)) allBoards = res;
            else if (res && res.data && Array.isArray(res.data.data)) allBoards = res.data.data;

            this.activePlans = allBoards
                .filter(b => b.status === 'active' || b.status === 'draft')
                .reverse(); 

        } catch (error) {
            console.error("Error fetching plans:", error);
            this.$vs.notify({color:'danger', text:'Could not load active plans'});
        } finally {
            this.isLoadingPlans = false;
        }
    },
    
    backToPlanList() {
        this.isModalActive = false; 
        setTimeout(() => {
            this.showActivePlansModal = true; 
        }, 200);
    },

    async openEditModal(plan) {
        this.isEditMode = true;
        this.editingBoardId = plan._id;
        this.selectedPlanName = "Existing Plan";
        this.boardTitle = plan.title;
        
        this.selectedUsers = [];
        this.originalBoardMembers = [];
        this.activeRoleFilters = [];
        
        this.showActivePlansModal = false;
        this.isModalActive = true;
        
        this.isLoadingBoardDetails = true;
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
            const url = `https://api.getboarded.tech/boards/${plan._id}`;
            
            const res = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const boardData = await res.json();
            
            const realMembers = boardData.members || [];
            
            realMembers.forEach(member => {
                const userEmail = member.email || (member.userData ? member.userData.email : '');
                const userId = member._id || (member.userData ? member.userData._id : '');

                const match = this.usersList.find(u => 
                    (u.email && userEmail && u.email === userEmail) || 
                    u.realUserId === userId || 
                    u.id === userId
                );
                
                if(match) {
                    this.selectedUsers.push(match.id);
                    this.originalBoardMembers.push(match.id);
                }
            });

        } catch(e) {
            console.error("Failed to load board details", e);
            this.$vs.notify({color:'warning', text:'Could not sync member list completely'});
        } finally {
            this.isLoadingBoardDetails = false;
        }
    },

    async fetchOrgUsers(orgId) {
      try {
        const res = await this.$store.dispatch("organization/getAllOrgEmployees", orgId);
        if (res && res.data) {
          this.usersList = res.data.map(employee => {
            let fname = '', lname = '', email = '';
            if (employee.userData) { 
                fname = employee.userData.firstName; 
                lname = employee.userData.lastName; 
                email = employee.userData.email; 
            }
            if (!fname) fname = employee.firstName; 
            if (!lname) lname = employee.lastName; 
            if (!email) email = employee.email;
            
            if ((!fname || !email) && employee.profileData?.personal_details) {
                const pd = employee.profileData.personal_details;
                fname = fname || pd.name?.firstName;
                lname = lname || pd.name?.lastName;
                if (!fname && pd.name?.fullName) { 
                    const parts = pd.name.fullName.split(' '); 
                    fname = parts[0]; 
                    lname = parts.slice(1).join(' '); 
                }
                email = email || pd.contactDetails?.emailId;
            }
            const finalName = (fname || lname) ? `${fname || ''} ${lname || ''}`.trim() : 'Unknown User';
            let realUserId = null;
            if (employee.userData && employee.userData._id) realUserId = employee.userData._id;
            else if (employee.userId) realUserId = employee.userId;

            let rawRole = employee.roleTitle || employee.designation || 'General';

            return { 
              id: employee._id,
              realUserId: realUserId,
              name: finalName, 
              email: email || '', 
              firstName: fname || '', 
              lastName: lname || '', 
              initials: this.getInitials(fname, lname), 
              role: this.toTitleCase(rawRole)
            };
          });
        }
      } catch (error) { console.error("Error fetching users:", error); }
    },

    async fetchRolesAndMapUsers(orgId) {
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
        const step1Res = await fetch(`https://api.getboarded.tech/kvdr?orgId=${orgId}`, { method: 'GET', headers });
        const step1Data = await step1Res.json();
        
        let kvdrId = null;
        if (step1Data.data && Array.isArray(step1Data.data) && step1Data.data.length > 0) kvdrId = step1Data.data[0]._id;
        else if (step1Data.data && step1Data.data._id) kvdrId = step1Data.data._id;

        if (!kvdrId) return;

        const step2Res = await fetch(`https://api.getboarded.tech/kvdr-role?kvdrId=${kvdrId}`, { method: 'GET', headers });
        const step2Data = await step2Res.json();

        if (step2Data && step2Data.data && Array.isArray(step2Data.data)) {
            this.orgDesignRoles = step2Data.data.map(t => this.toTitleCase(t.title));
            const userIdToRoleMap = {};
            step2Data.data.forEach(roleObj => {
                const roleTitle = this.toTitleCase(roleObj.title);
                if (roleObj.kvdrRoleProfiles && Array.isArray(roleObj.kvdrRoleProfiles)) {
                    roleObj.kvdrRoleProfiles.forEach(profile => {
                        if (profile.userId) userIdToRoleMap[profile.userId] = roleTitle;
                    });
                }
            });

            this.usersList = this.usersList.map(user => {
                if (user.realUserId && userIdToRoleMap[user.realUserId]) {
                    user.role = userIdToRoleMap[user.realUserId];
                } else if (userIdToRoleMap[user.id]) {
                    user.role = userIdToRoleMap[user.id];
                }
                return user;
            });
        }
      } catch (error) { console.error("Fetch Error:", error); }
    },

    toTitleCase(str) {
      if (!str) return '';
      return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    },
    getScoreLabel(s) { return s > 80 ? 'High Performing' : 'Developing'; },
    getScoreColor(s) { return s > 80 ? 'text-green-500' : 'text-orange-500'; },
    getProgressClass(s) { return s > 80 ? 'bg-green-500' : 'bg-orange-500'; },
    getInitials(f, l) { if(!f&&!l)return'??'; const a=f?f[0]:''; const b=l?l[0]:''; return (a+b).toUpperCase(); },
    generateBoardSlug(t) { let r = (Math.random() + 1).toString(36).substring(3); return t.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "") + "-" + r; },
    getRoleStyle(role) {
      const isSelected = this.activeRoleFilters.includes(role);
      if (isSelected) return { backgroundColor: '#FFC107', color: '#ffffff', borderColor: '#FFC107' };
      else return { backgroundColor: '#f1f5f9', color: '#475569', borderColor: 'transparent' };
    },

    openActivationModal(planName, planId, iconName) {
      this.isEditMode = false;
      this.editingBoardId = null;
      this.originalBoardMembers = [];
      this.selectedPlanName = planName;
      this.selectedPlanId = planId;
      this.selectedIcon = iconName;
      this.boardTitle = planName + " Workspace";
      this.selectedUsers = [];
      this.activeRoleFilters = []; 
      this.isModalActive = true;
      this.isLoading = false;
    },

    toggleUserSelection(user) {
      if (this.selectedUsers.includes(user.id)) this.selectedUsers = this.selectedUsers.filter(id => id !== user.id);
      else this.selectedUsers.push(user.id);
    },

    toggleRoleGroup(role) {
      if (this.activeRoleFilters.includes(role)) this.activeRoleFilters = this.activeRoleFilters.filter(r => r !== role);
      else this.activeRoleFilters.push(role);
    },

    async confirmActivation() {
      if (!this.boardTitle) return this.$vs.notify({ color: 'warning', text: 'Please enter a Plan Name.' });
      if (this.selectedUsers.length === 0) return this.$vs.notify({ color: 'warning', text: 'Select at least one participant.' });

      this.isLoading = true;

      if (this.isEditMode) {
          try {
             this.loadingStatusText = "Updating members...";
             
             const usersToAdd = this.usersList.filter(u => 
                this.selectedUsers.includes(u.id) && !this.originalBoardMembers.includes(u.id)
             );

             if(usersToAdd.length > 0) {
                 for (const user of usersToAdd) {
                   await this.$store.dispatch("boards/addUserToBoard", {
                     boardId: this.editingBoardId, 
                     email: user.email, 
                     firstName: user.firstName, 
                     lastName: user.lastName
                   });
                 }
                 this.$vs.notify({ color: 'success', title: 'Updated', text: `Added ${usersToAdd.length} new members.` });
             } else {
                 this.$vs.notify({ color: 'dark', text: 'No new members to add.' });
             }
             this.isModalActive = false;
          } catch(err) {
              console.error(err);
              this.$vs.notify({ color: 'danger', text: 'Update failed' });
          } finally {
              this.isLoading = false;
          }
          return;
      }

      try {
        this.loadingStatusText = "Creating Workspace...";
        const appsToInstall = this.planAppsMap[this.selectedPlanName] || this.defaultApps;
        
        const boardRes = await this.$store.dispatch("boards/createBoard", {
          title: this.boardTitle,
          boardId: 'custom-kanban-board', 
          userId: this.userData._id,
          organizationId: this.orgData._id,
          numberOfUsers: this.selectedUsers.length,
          installedApps: appsToInstall, 
          paymentStatus: "completed",
          status: "active",
          boardView: "kanban",
          slug: this.generateBoardSlug(this.boardTitle)
        });
        
        const newBoardId = boardRes.data._id;
        let usersToInvite = this.usersList.filter(u => this.selectedUsers.includes(u.id));
        
        for (const user of usersToInvite) {
           if(user.email === this.userData.email) continue;
           try {
             await this.$store.dispatch("boards/addUserToBoard", {
               boardId: newBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
             });
           } catch(e) { console.warn(e); }
        }

        this.loadingStatusText = "Done! Redirecting...";
        this.$vs.notify({ color: 'success', title: 'Plan Activated', text: 'Workspace created.' });
        setTimeout(() => this.$router.push(`/organization/${this.orgData.slug}/boards/${newBoardId}`), 800);
      } catch (error) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Setup failed.' });
        this.isLoading = false;
      }
    },
    
    goToBoard(plan) {
        this.$router.push(`/organization/${this.orgData.slug}/boards/${plan._id}`);
    }
  }
}
</script>

<style lang="scss" scoped>
.recommendations-container { background: #f8fafc; min-height: 100vh; }

/* Toggle Button Styling */
.view-toggle-btn {
  width: 40px;
  height: 40px;
  padding: 0 !important;
  display: flex !important;
  justify-content: center;
  align-items: center;
  border-radius: 8px !important;
}

/* Simple List View Styling */
.simple-list-row {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--vs-warning);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
    
    .icon-chevron-right {
      color: var(--vs-warning) !important;
      transform: translateX(4px);
    }
  }

  .icon-chevron-right {
    transition: transform 0.3s ease;
  }
}

/* Original Detailed Grid View Styling */
.recommendation-card { background: white; border-radius: 1.5rem; padding: 2rem; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); border: 1px solid #f1f5f9; height: 100%; display: flex; flex-direction: column; transition: all 0.3s ease; 
  &.recommended { border: 2px solid var(--vs-warning); background: linear-gradient(to bottom right, rgba(var(--vs-warning), 0.02), white); }
  &:hover { transform: translateY(-4px); box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12); }
}
.card-content { flex: 1; display: flex; flex-direction: column; }
.action-button { margin-top: auto; padding: 1rem; font-weight: 600; font-size: 1.1rem; border-radius: 0.75rem; }
.benefits-list li { margin-bottom: 1rem; padding-left: 2rem; position: relative; color: #475569; i { position: absolute; left: 0; top: 50%; transform: translateY(-50%); font-size: 1.25rem; } }

/* Shared Scroll and Popups Styling */
.custom-scroll::-webkit-scrollbar { width: 6px; }
.custom-scroll::-webkit-scrollbar-thumb { background: #ddd; border-radius: 3px; }

.role-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  user-select: none;
  &:hover { transform: translateY(-1px); filter: brightness(0.95); }
}

.active-plans-popup {
  ::v-deep .vs-popup {
    width: 550px;  
    max-width: 90vw;
    border-radius: 12px;
  }
  
  ::v-deep .vs-popup--content {
    padding: 0 !important;
    margin: 0 !important;
    width: 100%;
  }
  
  ::v-deep .vs-popup--header {
    background: #fff;
    border-bottom: 1px solid #f1f5f9;
    padding: 10px 15px; 
  }
}

.active-plans-content {
  min-height: 200px; 
  max-height: 55vh;
  overflow-y: auto;
  padding: 1.25rem;
}

.active-plans-footer {
  padding: 0.75rem 1.25rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.action-btn-gray:hover {
    background: #e2e8f0 !important;
}
</style>